import { useEffect, useState } from 'preact/hooks';
import { useFieldStatus } from '../lib/fieldStatus';
import {
  discardOutboxEntry,
  listOutbox,
  retryBlockedEntry,
  subscribeOutbox,
  type OutboxEntry,
} from '../lib/outbox';
import './FieldStatus.css';

// S5a §4 — the connectivity line and the waiting count, drawn exactly as
// wf03 draws them: `◌ no signal` as a FACT, and "N waiting · they will send
// themselves" as a reassurance.
//
// THE ONE RULE THIS COMPONENT EXISTS TO HOLD: no error styling on any
// offline state. Crimson (`--status-critical`) is reserved for URGENCY in
// this product, and a tech in a walk-in cooler is not an incident. The bar
// is a neutral wash at ten percent behind an ink label — no retry button,
// no apology, no red, and (since Huan's 2026-08-14 call) no amber either,
// because amber means CHASE and nothing here is chaseable. See the header
// of FieldStatus.css for why that departs from wf03's own drawing.
// FieldStatus.css mentions --status-critical nowhere, and the suite
// asserts that.
//
// It renders NOTHING when there is nothing to say (online, nothing waiting):
// a permanent "you are online" chip is chrome that earns no space on a
// phone.
//
// S5c ADDS THE ONE STATE THAT IS NOT PATIENCE. The waiting count is now real
// (lib/outbox), and beside it lives a second, different number: captures the
// server REFUSED. Those will never send themselves however long the tech
// waits, so they get the one affordance the offline state is forbidden — a
// way to act — and the distinction is deliberate:
//
//   waiting  → no button, no colour, no apology. Nothing has gone wrong.
//   refused  → the server's own words, a retry, and a way to let it go.
//
// Still ink, still no amber and no crimson: a refused capture needs a person,
// not an alarm, and the stylesheet is pinned against both tokens.
export function FieldStatus() {
  const { online, waiting, blocked } = useFieldStatus();
  const [open, setOpen] = useState(false);
  const [refused, setRefused] = useState<OutboxEntry[]>([]);

  useEffect(() => {
    let alive = true;
    const load = () => {
      void listOutbox().then((entries) => {
        if (alive) setRefused(entries.filter((e) => e.state === 'blocked'));
      });
    };
    load();
    const unsubscribe = subscribeOutbox(load);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);

  if (online && waiting === 0 && blocked === 0) return null;

  return (
    <div class="field-status" role="status">
      <div class="field-status__line">
        {online ? null : (
          <span class="field-status__sig" aria-label="No signal">
            <span aria-hidden="true">◌</span> No signal
          </span>
        )}
        {waiting > 0 ? (
          <span class="field-status__waiting">
            <b>{waiting} waiting</b> · they will send themselves
          </span>
        ) : blocked > 0 ? null : (
          // Offline with an empty pocket. Still worth saying, because the
          // alternative reads as "something is stuck" — and nothing is.
          <span class="field-status__waiting">· nothing is waiting to send</span>
        )}
        {blocked > 0 ? (
          <button
            type="button"
            class="field-status__blocked"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <b>
              {blocked} {blocked === 1 ? 'needs' : 'need'} you
            </b>{' '}
            · not sent
          </button>
        ) : null}
      </div>

      {open && refused.length > 0 ? (
        <ul class="field-status__refused">
          {refused.map((entry) => (
            <li key={entry.id} class="field-status__refused-item">
              <div class="field-status__refused-said">
                {entry.text.trim() ? `“${entry.text.trim()}”` : `${entry.files.length} file(s)`}
              </div>
              {/* The server's OWN words. This component has no idea why a
                  capture was refused and must not invent a reason — a made-up
                  explanation is the thing that sends a tech to the wrong fix. */}
              <div class="field-status__refused-why">
                {entry.lastError?.message ?? 'the server did not accept it'}
              </div>
              <div class="field-status__refused-acts">
                <button type="button" onClick={() => void retryBlockedEntry(entry.id)}>
                  Try again
                </button>
                {/* Discarding is also what RELEASES the queue: a refused entry
                    keeps its place in the order (nothing may skip ahead of it),
                    so anything captured after it waits until this one is
                    resolved one way or the other. */}
                <button type="button" onClick={() => void discardOutboxEntry(entry.id)}>
                  Let it go
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
